import React, { useState, useEffect, useRef } from 'react';
import { Box, Input, Spinner } from '@chakra-ui/react';
import { FiChevronDown } from 'react-icons/fi';
import api from '../lib/api';

function useOutsideClick(ref: React.RefObject<any>, handler: (event: Event) => void) {
  useEffect(() => {
    const listener = (event: Event) => {
      const el = ref?.current;
      if (!el || el.contains(event.target as Node)) {
        return;
      }
      handler(event);
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
}

interface AsyncTypeaheadProps {
  value: string;
  onChange: (value: string) => void;
  field: string; // 'brand_name' | 'disease_target' | 'category' | 'drug_group'
  placeholder?: string;
}

const AsyncTypeahead: React.FC<AsyncTypeaheadProps> = ({ value, onChange, field, placeholder }) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Sync internal state if prop changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useOutsideClick(wrapperRef, () => setIsOpen(false));

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (inputValue.length >= 3 && isOpen) {
        setIsLoading(true);
        try {
          const response = await api.get('/admin/master-drugs/search-attributes', {
            params: { field, q: inputValue }
          });
          setSuggestions(response.data);
        } catch (error) {
          console.error("Failed to fetch suggestions", error);
          setSuggestions([]);
        } finally {
          setIsLoading(false);
        }
      } else {
        setSuggestions([]);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [inputValue, field, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.value;
    setInputValue(newVal);
    onChange(newVal);
    if (newVal.length >= 3) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const handleSelect = (item: string) => {
    setInputValue(item);
    onChange(item);
    setIsOpen(false);
  };

  return (
    <Box position="relative" ref={wrapperRef} width="100%">
      <Input 
        value={inputValue} 
        onChange={handleInputChange} 
        placeholder={placeholder}
        onFocus={() => {
            if (inputValue.length >= 3) setIsOpen(true);
        }}
        autoComplete="off"
        paddingRight="40px"
      />
      <Box position="absolute" right={3} top="50%" transform="translateY(-50%)" zIndex={2} pointerEvents="none" color="gray.500">
         {isLoading ? <Spinner size="sm" /> : <FiChevronDown />}
      </Box>
      
      {isOpen && suggestions.length > 0 && (
        <Box 
          position="absolute" 
          top="100%" 
          left={0} 
          right={0} 
          bg="white" 
          boxShadow="md" 
          borderRadius="md" 
          maxH="200px" 
          overflowY="auto" 
          zIndex={50}
          mt={1}
          border="1px solid"
          borderColor="gray.200"
        >
          {suggestions.map((item, index) => (
            <Box 
              key={index} 
              px={4} 
              py={2} 
              cursor="pointer" 
              _hover={{ bg: 'gray.100' }}
              onClick={() => handleSelect(item)}
            >
              {item}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AsyncTypeahead;
